import {
  AdditiveBlending,
  BufferGeometry,
  CylinderGeometry,
  DoubleSide,
  Line,
  LineBasicMaterial,
  Mesh,
  MeshBasicMaterial,
  PointLight,
  RingGeometry,
  Vector3,
} from 'three';

const geometry = new CylinderGeometry(1, 1, 1, 24, 1, true);
const ringGeometry = new RingGeometry(0.65, 1, 48);
let beamState = null;
const arcScratch = new Vector3();

export function initBeams(world, options = {}) {
  beamState = {
    world,
    capacity: options.capacity ?? 24,
    beams: [],
    arcs: [],
    rings: [],
  };
  return beamState;
}

export function spawnBeam(position, options = {}) {
  if (!beamState || !position) return null;
  if (beamState.beams.length >= beamState.capacity) releaseBeam(0);

  const height = options.height ?? 26;
  const radius = options.radius ?? 0.5;
  const color = options.color ?? 0x6aa9ff;
  const material = new MeshBasicMaterial({
    color,
    transparent: true,
    opacity: options.opacity ?? 0.78,
    depthWrite: false,
    blending: AdditiveBlending,
  });
  const mesh = new Mesh(geometry, material);
  mesh.position.set(position.x, height * 0.5, position.z);
  mesh.scale.set(radius, height, radius);
  mesh.frustumCulled = false;
  const core = new Mesh(
    geometry,
    new MeshBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: options.coreOpacity ?? 0.58,
      depthWrite: false,
      blending: AdditiveBlending,
    }),
  );
  core.scale.set(0.32, 1.01, 0.32);
  mesh.add(core);

  const light = new PointLight(color, options.light ?? 7.5, options.lightRange ?? 11, 1.7);
  light.position.set(position.x, 1.7, position.z);
  mesh.add(light);

  const beam = {
    mesh,
    core,
    light,
    age: 0,
    ttl: options.ttl ?? 0.34,
    radius,
    height,
  };
  beamState.world.particleRoot.add(mesh);
  beamState.beams.push(beam);
  return beam;
}

export function spawnLightningArc(start, end, options = {}) {
  if (!beamState || !start || !end) return null;
  if (beamState.arcs.length >= beamState.capacity) releaseArc(0);

  const segments = options.segments ?? 7;
  const points = [];
  const height = options.height ?? 1.25;
  for (let i = 0; i <= segments; i += 1) {
    const t = i / segments;
    arcScratch.lerpVectors(start, end, t);
    arcScratch.y = height + Math.sin(t * Math.PI) * (options.arcHeight ?? 0.45);
    if (i > 0 && i < segments) {
      arcScratch.x += (Math.random() - 0.5) * (options.jitter ?? 0.55);
      arcScratch.z += (Math.random() - 0.5) * (options.jitter ?? 0.55);
    }
    points.push(arcScratch.clone());
  }

  const material = new LineBasicMaterial({
    color: options.color ?? 0x9cfffa,
    transparent: true,
    opacity: options.opacity ?? 0.92,
    blending: AdditiveBlending,
    depthWrite: false,
  });
  const line = new Line(new BufferGeometry().setFromPoints(points), material);
  line.frustumCulled = false;
  const arc = { line, age: 0, ttl: options.ttl ?? 0.18 };
  beamState.world.particleRoot.add(line);
  beamState.arcs.push(arc);
  return arc;
}

export function spawnExplosionRing(position, options = {}) {
  if (!beamState || !position) return null;
  if (beamState.rings.length >= beamState.capacity) releaseRing(0);

  const color = options.color ?? 0xffd166;
  const material = new MeshBasicMaterial({
    color,
    transparent: true,
    opacity: options.opacity ?? 0.86,
    blending: AdditiveBlending,
    depthWrite: false,
    side: DoubleSide,
  });
  const mesh = new Mesh(ringGeometry, material);
  mesh.position.set(position.x, options.y ?? 0.08, position.z);
  mesh.rotation.x = -Math.PI / 2;
  mesh.scale.setScalar(options.startScale ?? 0.55);
  mesh.frustumCulled = false;
  const inner = new Mesh(
    ringGeometry,
    new MeshBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0.34,
      blending: AdditiveBlending,
      depthWrite: false,
      side: DoubleSide,
    }),
  );
  inner.scale.setScalar(0.62);
  mesh.add(inner);
  const ring = {
    mesh,
    inner,
    age: 0,
    ttl: options.ttl ?? 0.48,
    maxScale: options.maxScale ?? 4.2,
  };
  beamState.world.particleRoot.add(mesh);
  beamState.rings.push(ring);
  return ring;
}

export function updateBeams(dt) {
  if (!beamState) return;
  for (const beam of beamState.beams) {
    beam.age += dt;
    const t = Math.min(1, beam.age / Math.max(0.001, beam.ttl));
    const pulse = 1 + Math.sin(t * Math.PI) * 1.1;
    beam.mesh.scale.set(beam.radius * pulse, beam.height, beam.radius * pulse);
    beam.mesh.material.opacity = (1 - t) * 0.82;
    beam.core.material.opacity = (1 - t) * 0.62;
    beam.light.intensity = (1 - t) * 8;
  }
  for (let i = beamState.beams.length - 1; i >= 0; i -= 1) {
    if (beamState.beams[i].age >= beamState.beams[i].ttl) releaseBeam(i);
  }
  for (const arc of beamState.arcs) {
    arc.age += dt;
    const t = Math.min(1, arc.age / Math.max(0.001, arc.ttl));
    arc.line.material.opacity = (1 - t) * 0.95;
  }
  for (let i = beamState.arcs.length - 1; i >= 0; i -= 1) {
    if (beamState.arcs[i].age >= beamState.arcs[i].ttl) releaseArc(i);
  }
  for (const ring of beamState.rings) {
    ring.age += dt;
    const t = Math.min(1, ring.age / Math.max(0.001, ring.ttl));
    const eased = 1 - (1 - t) * (1 - t);
    ring.mesh.scale.setScalar(0.35 + eased * ring.maxScale);
    ring.mesh.material.opacity = (1 - t) * 0.86;
    ring.inner.material.opacity = (1 - t) * 0.38;
  }
  for (let i = beamState.rings.length - 1; i >= 0; i -= 1) {
    if (beamState.rings[i].age >= beamState.rings[i].ttl) releaseRing(i);
  }
}

function releaseBeam(index) {
  const beam = beamState.beams[index];
  if (!beam) return;
  beamState.world.particleRoot.remove(beam.mesh);
  beam.core.material.dispose();
  beam.mesh.material.dispose();
  beamState.beams.splice(index, 1);
}

function releaseArc(index) {
  const arc = beamState.arcs[index];
  if (!arc) return;
  beamState.world.particleRoot.remove(arc.line);
  arc.line.geometry.dispose();
  arc.line.material.dispose();
  beamState.arcs.splice(index, 1);
}

function releaseRing(index) {
  const ring = beamState.rings[index];
  if (!ring) return;
  beamState.world.particleRoot.remove(ring.mesh);
  ring.inner.material.dispose();
  ring.mesh.material.dispose();
  beamState.rings.splice(index, 1);
}
